/**
 * AssistantPanel — sheet 22's docked panel (mission 8 WP08 T040, T043; FR-004, FR-005, FR-006,
 * FR-012, FR-014, FR-016, FR-025; SAT.1, SAT.5, SAT.9, AV.8, D.2, GEN.4; NFR-006, NFR-010; C-007,
 * C-008, C-012; amendments 14 and 15; OR-9, OR-10, OR-11; squad U11, U12, U14; post-tasks TR-1, P13;
 * research §1.19 to §1.22; style guide §7.12, §9).
 *
 * WP09 mounts it through `AssistantDock` on Results and Rejected; it renders only while open.
 *
 * - **Header (U14).** Row 1 (56 px): the `h3` title and Close. Row 2 (caption): the context chip
 *   (mono 12, ellipsis, the full text in its `aria-label`), the rule-context chip with its remove
 *   control (OR-9), and the PII disclosure button (`aria-expanded`, `aria-controls`; FR-014).
 * - **Reads (U11, NFR-010).** One `useQuery(usageQuery())` here, passed down; the conversation's
 *   infinite query, page 1 on open, older pages on scroll to the top or on `assistantLoadOlder`.
 * - **Live regions (U12).** The log is `role="log"`, polite, `aria-busy` until page 1 settles and
 *   while an older page loads; one `role="status"` region says `assistantAnswering`, then
 *   `assistantAnswerReceived`.
 * - **Throttled (TR-1).** The notice replaces the meter and the composer when the usage read's
 *   settled count has reached the ceiling, or on a 429; a busy 503 never shows it (amendment 15).
 * - **Other refusals** render through `resolveProblem`: busy (503 SAT.5) and unavailable (503 AV.8)
 *   are warning bands above the composer, the conversation still readable. The run's data gone
 *   (D.2), the run not ready (GEN.4) and no credit cycle (SAT.5) disable the composer and say why.
 *   A failed conversation read shows `assistantReadFailed` with Retry. While page 1 is pending or has
 *   failed, Send is disabled and says why (the log's `loading` caption, or that notice), because an
 *   answer has no page to join (mission 8 review UI1). A failed usage read with no data shows `assistantUsageUnavailable` in
 *   place of the meter (UI2).
 * - **Focus (OR-10, FR-025).** On open, the composer after the next frame (the drawer hand-off's
 *   frame); whenever the composer becomes unusable (replaced or disabled) with focus lost or on a
 *   disabled control, Close (UI3); Esc with focus inside closes. What happens after any close (Close,
 *   Esc or Back) is `AssistantDock`'s: the conversation removed (P13) and focus returned to the rail or
 *   the `h1`.
 * - **Swept (mission 10 WP18 T105; FR-019 22/11's Trellis half, FR-020, C-002; spec amendment 21).** When
 *   the run's Class 1 data is deleted (the run read's `class1_deleted_at`, observed from the page's cache entry
 *   without a read of its own, or a `run-data-gone` answer), register 37 `conversationRemoved` stands in place
 *   of the conversation and the composer stays disabled with the D.2 reason. AgentCore memory is Satori's,
 *   deferred, and not claimed (OR-15, U19).
 *
 * - **Standing disclaimer (SAT.7; counsel memo 2026-10-03, B3).** `SAT_STANDING_DISCLAIMER` renders once,
 *   verbatim and never dismissible, at the end of the footer in every state, throttled included.
 *
 * The response-mode note and the feedback comment are not built (C-008). One identity, no roles
 * (C-007); desktop only (C-012).
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type UIEvent } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { Band, type BandKind } from '@/components/trellis/Band'
import { DisclaimerBand } from '@/components/trellis/DisclaimerBand'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError, type Problem } from '@/lib/api'
import { useAssistantPanel } from '@/lib/assistant-panel'
import { Mono, date, shortId } from '@/lib/format'
import { SAT_STANDING_DISCLAIMER } from '@/lib/mandatory-copy'
import { gateKey } from '@/lib/problems'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'
import { conversationInfiniteQuery, runQuery, useAskMutation, usageQuery } from '@/lib/queries'

import { AssistantComposer } from './AssistantComposer'
import { AssistantMessage } from './AssistantMessage'
import { UsageMeter } from './UsageMeter'

/** Answered messages a run keeps (C-010): `KeptPerRun` in backend/internal/domain/assistant/message.go. */
export const CONVERSATION_KEPT = 200

/** Why the composer is disabled (research §1.3, §1.13). */
type Reason = 'run-data-gone' | 'run-not-ready' | 'no-cycle'

const REASONS: Record<Reason, { kind: BandKind; text: TextKey; requirement: string }> = {
  'run-data-gone': { kind: 'info', text: 'assistantRunDataGone', requirement: 'D.2' },
  'run-not-ready': { kind: 'info', text: 'assistantRunNotReady', requirement: 'GEN.4' },
  'no-cycle': { kind: 'blocked', text: 'assistantNoCycle', requirement: 'SAT.5' },
}

const PROBLEM_REASONS: Record<string, Reason> = {
  'assistant-unavailable|409|D.2': 'run-data-gone',
  'run-not-ready|409|GEN.4': 'run-not-ready',
  'assistant-unavailable|409|SAT.5': 'no-cycle',
}

function problemOf(error: unknown): Problem | null {
  return error instanceof ApiError ? error.problem : null
}

function reasonOf(problem: Problem | null): Reason | null {
  return problem === null ? null : (PROBLEM_REASONS[gateKey(problem)] ?? null)
}

export interface AssistantPanelProps {
  runId: string
  /** The organization's name, for the context chip. */
  tenantName: string
  /** The run's WARN findings, for the triage prompt (hidden at 0). */
  warnCount: number
}

export function AssistantPanel({ runId, tenantName, warnCount }: AssistantPanelProps) {
  const { ruleContext, clearRuleContext, closePanel } = useAssistantPanel()
  const usage = useQuery(usageQuery())
  const conversation = useInfiniteQuery(conversationInfiniteQuery(runId))
  const ask = useAskMutation()
  // T105: the page's run read, observed only (enabled: false issues no request); the page owns the poll.
  const runRead = useQuery({ ...runQuery(runId), enabled: false })
  const [piiOpen, setPiiOpen] = useState(false)

  const id = useId()
  const titleId = `${id}-title`
  const piiId = `${id}-pii`
  const reasonId = `${id}-reason`
  const loadingId = `${id}-loading`
  const readFailedId = `${id}-read-failed`
  const composer = useRef<HTMLTextAreaElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const log = useRef<HTMLDivElement>(null)

  // OR-10: the composer after the hand-off frame; Close when the composer is replaced or disabled.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const box = composer.current
      ;(box !== null && !box.disabled ? box : closeButton.current)?.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  // Every close (Close, Esc, or Back dropping ?assistant=open) is finished by AssistantDock: the
  // conversation removed and focus returned (P13, OR-10).
  function close() {
    closePanel()
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Escape') return
    event.stopPropagation()
    close()
  }

  const pages = conversation.data?.pages ?? []
  const messages = pages.flatMap((p) => p.messages).reverse()
  const newest = messages.at(-1)?.id
  const trimmed = pages.some((p) => p.trimmed) && !conversation.hasNextPage
  const olderLoading = conversation.isFetchingNextPage

  useEffect(() => {
    const el = log.current
    if (el !== null) el.scrollTop = el.scrollHeight
  }, [newest])

  // A burst of scroll events joins the page already in flight instead of cancelling it (NFR-010).
  function loadOlder() {
    if (conversation.hasNextPage) void conversation.fetchNextPage({ cancelRefetch: false })
  }

  function onScroll(event: UIEvent<HTMLDivElement>) {
    if (event.currentTarget.scrollTop === 0) loadOlder()
  }

  // What the refusals say (TR-1, OR-11; research §1.3).
  const askProblem = problemOf(ask.error)
  const page1 = pages[0]
  const reason: Reason | null =
    (runRead.data?.class1_deleted_at ? 'run-data-gone' : null) ??
    (page1 !== undefined && !page1.available ? page1.unavailable_reason : null) ??
    reasonOf(problemOf(usage.error)) ??
    reasonOf(askProblem)
  const settled = usage.data
  const throttle =
    settled !== undefined && settled.used >= settled.ceiling
      ? { ceiling: settled.ceiling, resetsAt: settled.resets_at }
      : askProblem?.status === 429 && askProblem.ceiling !== undefined && askProblem.resets_at !== undefined
        ? { ceiling: askProblem.ceiling, resetsAt: askProblem.resets_at }
        : null

  // OR-10 (mission 8 WP14, join; mission 8 review UI3): the notice may replace the composer, or a reason
  // disable it, after it took focus on the open frame (the usage read or page 1 lands later). A removed
  // element leaves focus on the body and a disabled one keeps a focus nothing can act on, so it moves to
  // Close. A refetch that leaves the composer usable changes nothing, so focus is never stolen.
  const usable = throttle === null && reason === null
  // T105 (22/11): the run was swept, so its conversation is gone.
  const removed = reason === 'run-data-gone'
  useEffect(() => {
    if (usable) return
    const active = document.activeElement
    if (active === null || active === document.body || (active instanceof HTMLElement && active.matches(':disabled')))
      closeButton.current?.focus()
  }, [usable])

  function onSend(question: string) {
    return ask.mutateAsync(
      ruleContext === null ? { run_id: runId, question } : { run_id: runId, rule_id: ruleContext, question },
    )
  }

  const run = shortId(runId)
  const status = ask.isPending ? copyText('assistantAnswering') : ask.isSuccess ? copyText('assistantAnswerReceived') : ''

  let footer
  if (throttle !== null) {
    footer = (
      <Band kind="blocked" requirement="SAT.5" data-assistant-throttled="">
        {PROVISIONAL_COPY.assistantThrottled.render({ ceiling: throttle.ceiling, resetsAt: date(throttle.resetsAt) })}
      </Band>
    )
  } else if (askProblem?.status === 429) {
    footer = <ProblemBand problem={askProblem} />
  } else {
    const r = reason === null ? null : REASONS[reason]
    footer = (
      <>
        {ask.isError && reasonOf(askProblem) === null ? (
          askProblem !== null ? (
            <ProblemBand problem={askProblem} />
          ) : (
            <Band kind="blocked">{copyText('genericError')}</Band>
          )
        ) : null}
        {r !== null ? (
          <Band kind={r.kind} requirement={r.requirement} id={reasonId} data-assistant-reason={reason}>
            {copyText(r.text)}
          </Band>
        ) : null}
        {settled !== undefined ? (
          <UsageMeter usage={settled} />
        ) : usage.isError && reasonOf(problemOf(usage.error)) === null ? (
          <p data-assistant-usage-unavailable="" className="text-caption text-navy-600">
            {copyText('assistantUsageUnavailable')}
          </p>
        ) : null}
        <AssistantComposer
          ref={composer}
          onSend={onSend}
          sending={ask.isPending}
          showPrompts={conversation.isSuccess && messages.length === 0}
          warnCount={warnCount}
          disabledReasonId={r !== null ? reasonId : undefined}
          sendWaitsFor={conversation.isPending ? loadingId : conversation.isError && !removed ? readFailedId : undefined}
        />
      </>
    )
  }

  return (
    <section
      aria-labelledby={titleId}
      data-assistant-panel=""
      className="flex h-full min-h-0 flex-col"
      onKeyDown={onKeyDown}
    >
      <header className="border-b border-navy-200">
        <div className="flex h-14 items-center justify-between gap-3 px-4">
          <h3 id={titleId} className="text-h3 text-navy-950">
            {copyText('assistantTitle')}
          </h3>
          <Button
            ref={closeButton}
            variant="ghost"
            size="icon"
            className="size-8"
            data-assistant-close=""
            aria-label={copyText('assistantClose')}
            onClick={close}
            icon={<X aria-hidden />}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3 text-caption">
          <span
            role="group"
            aria-label={PROVISIONAL_COPY.assistantContextLabel.render({ run, tenant: tenantName })}
            data-context-chip=""
            className="min-w-0 max-w-full truncate rounded-full bg-navy-100 px-2.5 py-1 font-mono text-caption text-navy-950"
          >
            {/* Mission 8 review UI4: no tenant part while the tenant read loads or fails. */}
            <span aria-hidden>{tenantName === '' ? run : `${run} · ${tenantName}`}</span>
          </span>
          {ruleContext !== null ? (
            <span data-rule-chip="" className="inline-flex items-center gap-0.5 rounded-full bg-navy-100 pl-2.5 text-navy-950">
              <Mono className="text-caption">{ruleContext}</Mono>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 rounded-full"
                aria-label={PROVISIONAL_COPY.assistantRuleChipRemove.render({ ruleId: ruleContext })}
                onClick={clearRuleContext}
                icon={<X aria-hidden />}
              />
            </span>
          ) : null}
          <button
            type="button"
            aria-expanded={piiOpen}
            aria-controls={piiId}
            onClick={() => setPiiOpen((o) => !o)}
            className="inline-flex min-h-8 items-center rounded-sm text-caption text-[var(--color-link)] underline hover:text-[var(--color-link-hover)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
          >
            {copyText('assistantPiiLink')}
          </button>
        </div>
        <p id={piiId} hidden={!piiOpen} className="px-4 pb-3 text-caption text-navy-700">
          {copyText('assistantPiiStatement')}
        </p>
      </header>

      <div
        ref={log}
        role="log"
        aria-live="polite"
        aria-label={copyText('assistantConversationLabel')}
        aria-busy={conversation.isPending || olderLoading ? true : undefined}
        onScroll={onScroll}
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4"
      >
        {conversation.isPending ? (
          <div className="flex flex-col gap-3">
            {/* Review cycle 1, finding 1 (style guide §7, §11): the stated reason Send waits for. */}
            <p id={loadingId} className="text-caption text-navy-600">
              {copyText('loading')}
            </p>
            <Skeleton className="ml-auto h-10 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : removed ? (
          <p data-conversation-removed="" className="text-body text-navy-600">
            {copyText('conversationRemoved')}
          </p>
        ) : conversation.isError ? (
          <Band
            kind="blocked"
            action={
              <Button variant="secondary" size="sm" onClick={() => void conversation.refetch()}>
                {copyText('retry')}
              </Button>
            }
          >
            <span id={readFailedId}>{copyText('assistantReadFailed')}</span>
          </Band>
        ) : (
          <>
            {trimmed ? (
              <p className="text-caption text-navy-600">
                {PROVISIONAL_COPY.assistantTrimmed.render({ kept: CONVERSATION_KEPT })}
              </p>
            ) : null}
            {conversation.hasNextPage ? (
              <Button variant="ghost" size="sm" className="self-center" loading={olderLoading} onClick={loadOlder}>
                {copyText('assistantLoadOlder')}
              </Button>
            ) : null}
            {messages.length === 0 && page1?.available !== false ? (
              <p className="text-body text-navy-600">{copyText('assistantEmpty')}</p>
            ) : null}
            <ul className="flex flex-col gap-6">
              {messages.map((m) => (
                <AssistantMessage key={m.id} message={m} />
              ))}
            </ul>
          </>
        )}
      </div>

      <div role="status" data-assistant-status="" className="sr-only">
        {status}
      </div>

      <div className="flex flex-col gap-3 border-t border-navy-200 px-4 py-3">
        {footer}
        {/* SAT.7, counsel B3: the standing disclaimer, once, verbatim, never dismissible. */}
        <div data-assistant-disclaimer="">
          <DisclaimerBand variant="caption">{SAT_STANDING_DISCLAIMER}</DisclaimerBand>
        </div>
      </div>
    </section>
  )
}
